import React, { useState } from "react";
import { Calendar, ExternalLink, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { getCoachCalendarDebug } from "../../lib/api";

/**
 * CalendarFeedInspector — coach-only diagnostic panel. Lets the coach view
 * exactly what events Apple/Google would receive for a given athlete's
 * calendar subscription. Surfaces the most common "subscribed but empty"
 * causes (missing start_date, drip too low, no programs assigned).
 */
export default function CalendarFeedInspector({ athleteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getCoachCalendarDebug(athleteId);
      setData(d);
      setExpanded(true);
    } catch {
      toast.error("Couldn't load calendar feed info");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="bg-white border border-[var(--tir-border)] p-4"
      data-testid="calendar-feed-inspector"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2">
          <Calendar size={15} className="text-[var(--tir-navy)]" />
          <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-ink)]">
            Athlete calendar feed
          </span>
          <span className="text-[10px] text-[var(--tir-muted)] italic normal-case tracking-normal">
            — what their Apple/Google Calendar receives
          </span>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="inspect-calendar-feed-btn"
          className="inline-flex items-center gap-1.5 border border-[var(--tir-navy)] bg-white hover:bg-[var(--tir-navy)] hover:text-white transition-colors px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] disabled:opacity-50"
        >
          {loading ? "Loading…" : data ? "Refresh" : "Inspect feed"}
        </button>
      </div>

      {data && expanded && (
        <div className="mt-4 space-y-4">
          {/* Headline status */}
          <div
            className={`border-l-4 pl-3 py-1 ${
              data.total_events_in_feed > 0
                ? "border-[#1e7d4a] bg-[#f0f9f4]"
                : "border-[var(--tir-burgundy)] bg-[#fdf6f4]"
            }`}
            data-testid="calendar-debug-headline"
          >
            <div className="inline-flex items-center gap-1.5 font-serif-display text-lg">
              {data.total_events_in_feed > 0 ? (
                <CheckCircle2 size={16} className="text-[#1e7d4a]" />
              ) : (
                <AlertCircle size={16} className="text-[var(--tir-burgundy)]" />
              )}
              <span>
                {data.total_events_in_feed}{" "}
                {data.total_events_in_feed === 1 ? "event" : "events"} in feed
              </span>
            </div>
            {data.total_events_in_feed === 0 && (
              <div className="text-[12px] text-[var(--tir-burgundy)] mt-1">
                {data.programs_count === 0
                  ? "This athlete has no programs assigned yet. Once you create or assign one, workouts will appear."
                  : "Programs are assigned but no events are inside the drip window. See per-program detail below — usually the cause is a missing start date or drip set too low."}
              </div>
            )}
          </div>

          {/* Athlete settings */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
            <Stat label="Drip release" value={`${data.drip_weeks} wk`} />
            <Stat label="Event time" value={data.calendar_event_time} />
            <Stat label="Programs" value={data.programs_count} />
            <Stat label="Today" value={data.today} />
          </div>

          {/* Per-program breakdown */}
          {data.programs && data.programs.length > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Programs scanned
              </div>
              <div className="border-t border-[var(--tir-border)]">
                {data.programs.map((p) => (
                  <div
                    key={p.program_id}
                    className="grid grid-cols-12 gap-2 py-2 border-b border-[var(--tir-border)] text-[12px]"
                    data-testid={`debug-program-${p.program_id}`}
                  >
                    <div className="col-span-12 sm:col-span-5 truncate text-[var(--tir-ink)] font-semibold">
                      {p.title}
                    </div>
                    <div className="col-span-6 sm:col-span-3 text-[var(--tir-muted)] font-mono-tir">
                      {p.start_date || (
                        <span className="text-[var(--tir-burgundy)]">
                          ⚠ no start date
                        </span>
                      )}
                      {p.fallback_to_today_used && (
                        <span className="text-[var(--tir-burgundy)] ml-1">
                          → today
                        </span>
                      )}
                    </div>
                    <div className="col-span-3 sm:col-span-2 text-[var(--tir-muted)]">
                      Wk {p.current_week_index + 1}/{p.total_weeks}
                    </div>
                    <div
                      className={`col-span-3 sm:col-span-2 text-right font-mono-tir font-semibold ${
                        p.events_in_window > 0
                          ? "text-[#1e7d4a]"
                          : "text-[var(--tir-burgundy)]"
                      }`}
                    >
                      {p.events_in_window} events
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sample events */}
          {data.sample_events && data.sample_events.length > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Sample events (first {data.sample_events.length})
              </div>
              <div className="border-t border-[var(--tir-border)]">
                {data.sample_events.map((e, i) => (
                  <div
                    key={i}
                    className="flex items-baseline justify-between gap-3 py-1.5 border-b border-[var(--tir-border)] text-[12px]"
                  >
                    <span className="font-mono-tir text-[var(--tir-muted)] shrink-0">
                      {e.date}
                    </span>
                    <span className="flex-1 truncate text-[var(--tir-ink)]">
                      {e.title}
                    </span>
                    <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
                      {e.type || ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw feed URL */}
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
              Subscription URL
              {data.athlete_name ? ` (${data.athlete_name}'s)` : ""}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={data.feed_url}
                onFocus={(e) => e.target.select()}
                data-testid="debug-feed-url"
                className="flex-1 font-mono-tir text-[11px] border border-[var(--tir-border)] bg-[var(--tir-ivory)] px-2 py-1.5 text-[var(--tir-muted)]"
              />
              <a
                href={data.feed_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="debug-open-feed"
                className="inline-flex items-center gap-1 border border-[var(--tir-navy)] bg-white hover:bg-[var(--tir-navy)] hover:text-white transition-colors px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-navy)] whitespace-nowrap"
              >
                <ExternalLink size={11} /> Open raw
              </a>
            </div>
            <p className="text-[11px] italic text-[var(--tir-muted)] mt-1">
              "Open raw" shows the actual .ics content the calendar app receives.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[var(--tir-border)] bg-[var(--tir-ivory)] px-2.5 py-1.5">
      <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
        {label}
      </div>
      <div className="font-mono-tir text-sm text-[var(--tir-ink)]">{value}</div>
    </div>
  );
}
